import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useTranslation } from 'react-i18next';

const CustomerRegisterPageV2: React.FC = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated, loading } = useCustomerAuth();
  const { t, i18n } = useTranslation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    company: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Automatic redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate('/customer/plans');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError(t('customer.auth.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('customer.auth.passwordTooShort'));
      return;
    }

    setIsLoading(true);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        company: formData.company || undefined,
        phone: formData.phone || undefined
      });
      
      // Small delay before navigation to ensure state updates
      setTimeout(() => {
        navigate('/customer/plans');
      }, 100);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('customer.auth.registrationError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (event: any) => {
    const newLang = event.target.value;
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: '#f8f9fa',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid #e0e0e0',
            borderRadius: 2
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: '#1a1a1a'
              }}
            >
              {t('customer.auth.createAccount')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('customer.auth.registerSubtitle')}
            </Typography>
          </Box>

          {/* Language Selector */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{t('customer.auth.language')}</InputLabel>
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              label={t('customer.auth.language')}
            >
              <MenuItem value="es">Español</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </Select>
          </FormControl>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Register Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('customer.auth.fullName')}
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('customer.auth.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('customer.auth.company')}
              name="company"
              value={formData.company}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('customer.auth.phone')}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('customer.auth.password')}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              helperText={t('customer.auth.passwordHelp')}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('customer.auth.confirmPassword')}
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                borderRadius: 2,
                boxShadow: 'none'
              }}
            >
              {isLoading ? t('customer.auth.creatingAccount') : t('customer.auth.signUp')}
            </Button>
          </form>

          {/* Footer Links */}
          <Box textAlign="center" mt={3}>
            <Typography variant="body2" color="text.secondary">
              {t('customer.auth.haveAccount')}{' '}
              <Link
                onClick={() => navigate('/customer/login')}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  textDecoration: 'none',
                  color: 'primary.main',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {t('auth.login')}
              </Link>
            </Typography>
          </Box>
        </Paper>

        {/* Back to Home */}
        <Box textAlign="center" mt={3}>
          <Link
            onClick={() => navigate('/customer')}
            sx={{
              cursor: 'pointer',
              color: 'text.secondary',
              textDecoration: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                color: 'primary.main'
              }
            }}
          >
            ← {t('customer.auth.backToHome')}
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default CustomerRegisterPageV2;

